import type {
  ConfirmStripeSessionRequest,
  ConfirmStripeSessionResponse,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  PaymentState,
  ReportStatus,
  StartIntakeRequest,
  StartIntakeResponse,
  StatusResponse,
  SubmitIntakeRequest,
  SubmitIntakeResponse,
  UploadSessionRequest,
  UploadSessionResponse,
  ValidateUploadRequest,
  ValidateUploadResponse,
} from "@/types/api";
import type { IntakeApi } from "./types";

const STORE_KEY = "r1-intake-mock-store";
const PROCESSING_MS = 20_000;

interface MockReport {
  reportId: string;
  offerId: string;
  email?: string;
  submittedAt?: number;
  paymentState: PaymentState;
}

type MockStore = Record<string, MockReport>;

function readStore(): MockStore {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "{}") as MockStore;
  } catch {
    return {};
  }
}

function writeStore(store: MockStore): void {
  localStorage.setItem(STORE_KEY, JSON.stringify(store));
}

function upsert(report: MockReport): void {
  const store = readStore();
  store[report.reportId] = report;
  writeStore(store);
}

function getReport(reportId: string): MockReport | undefined {
  return readStore()[reportId];
}

function delay<T>(value: T, ms = 350): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

function newId(): string {
  return `mock-${Math.random().toString(36).slice(2, 8)}-${Date.now().toString(36)}`;
}

function computeStatus(report: MockReport): ReportStatus {
  if (report.paymentState === "paid") return "paid";
  if (!report.submittedAt) return "processing";
  return Date.now() - report.submittedAt >= PROCESSING_MS ? "preview_ready" : "processing";
}

interface MockOfferPricing {
  minimumPriceCents: number;
  basePriceCents: number;
  pricePerThousandCharsCents: number;
  includedCharacters: number;
}

const MOCK_OFFER_PRICING: Record<string, MockOfferPricing> = {
  "obligations-export": {
    minimumPriceCents: 29700,
    basePriceCents: 29700,
    pricePerThousandCharsCents: 250,
    includedCharacters: 25_000,
  },
  "sop-coverage": {
    minimumPriceCents: 59700,
    basePriceCents: 59700,
    pricePerThousandCharsCents: 600,
    includedCharacters: 50_000,
  },
};

function mockOfferPricing(offerId: string): MockOfferPricing {
  return (
    MOCK_OFFER_PRICING[offerId] ?? {
      minimumPriceCents: 4900,
      basePriceCents: 2900,
      pricePerThousandCharsCents: 50,
      includedCharacters: 25_000,
    }
  );
}

function buildMockPricingBreakdown(offerId: string, totalCharacters: number) {
  const offer = mockOfferPricing(offerId);
  const billable = Math.max(0, totalCharacters - offer.includedCharacters);
  const volumeUnits = billable <= 0 ? 0 : Math.ceil(billable / 1000);
  const volumeComponentCents = volumeUnits * offer.pricePerThousandCharsCents;
  const computedCents = offer.basePriceCents + volumeComponentCents;
  const priceCents = Math.max(computedCents, offer.minimumPriceCents);
  return {
    billableCharacters: billable,
    includedCharacters: offer.includedCharacters,
    pricingBreakdown: {
      baseComponentCents: offer.basePriceCents,
      volumeComponentCents,
      computedCents,
      priceCents,
      minimumApplied: priceCents > computedCents,
    },
    quotedPriceCents: priceCents,
  };
}

/** Used by the success screen to simulate the Stripe webhook unlocking the report. */
export function markReportPaidMock(reportId: string): void {
  const report = getReport(reportId);
  if (report) upsert({ ...report, paymentState: "paid" });
}

export function createMockClient(): IntakeApi {
  return {
    async startIntake(req: StartIntakeRequest): Promise<StartIntakeResponse> {
      const reportId = newId();
      upsert({ reportId, offerId: req.offerId, paymentState: "unpaid" });
      return delay({ reportId });
    },

    async createUploadSession(req: UploadSessionRequest): Promise<UploadSessionResponse> {
      return delay({
        reportId: req.reportId,
        uploads: req.files.map((f, i) => ({
          fileName: f.fileName,
          uploadUrl: `mock://upload/${req.reportId}/${i}`,
          blobName: `${req.reportId}/${Date.now()}-${f.fileName}`,
        })),
      });
    },

    async validateUpload(_req: ValidateUploadRequest): Promise<ValidateUploadResponse> {
      return delay({ characterCount: 12_000, approxPages: 7 });
    },

    async submitIntake(req: SubmitIntakeRequest): Promise<SubmitIntakeResponse> {
      const existing = getReport(req.reportId);
      upsert({
        reportId: req.reportId,
        offerId: req.offerId,
        email: req.email,
        submittedAt: Date.now(),
        paymentState: existing?.paymentState ?? "unpaid",
      });
      return delay({ reportId: req.reportId, status: "processing" as ReportStatus });
    },

    async getStatus(reportId: string): Promise<StatusResponse> {
      const report = getReport(reportId);
      if (!report) {
        return delay({
          reportId,
          status: "processing" as ReportStatus,
          paymentState: "unpaid" as PaymentState,
          message: "We're getting things ready.",
          retryAfterMs: 4000,
        });
      }
      const status = computeStatus(report);
      const ready = status === "preview_ready" || status === "paid";
      const totalCharacters = 49_440;
      const pricing = ready ? buildMockPricingBreakdown(report.offerId, totalCharacters) : null;
      const isCoverage = report.offerId !== "obligations-export";

      return delay({
        reportId,
        offerId: report.offerId,
        status,
        paymentState: report.paymentState,
        retryAfterMs: status === "processing" ? 4000 : undefined,
        quotedPriceCents: pricing?.quotedPriceCents,
        quotedCurrency: ready ? "eur" : undefined,
        totalCharacters: ready ? totalCharacters : undefined,
        includedCharacters: pricing?.includedCharacters,
        billableCharacters: pricing?.billableCharacters,
        pricingBreakdown: pricing?.pricingBreakdown,
        findingCount: ready ? (isCoverage ? 42 : 239) : undefined,
        gapCount: ready && isCoverage ? 11 : undefined,
        documentCount: ready ? (isCoverage ? 4 : 1) : undefined,
        previewDownloadUrl: undefined,
        fullDownloadUrl: report.paymentState === "paid" ? "mock://download/word" : undefined,
        fullExcelDownloadUrl: report.paymentState === "paid" ? "mock://download/excel" : undefined,
        preview: ready
          ? isCoverage
            ? {
                title: "Your gap analysis preview is ready",
                summary:
                  "Preview: 10 gap example(s). Full analysis: 42 findings, 11 requiring review.",
                highlights: [
                  "11 gap(s) requiring review",
                  "42 finding(s) across 4 document(s)",
                  "About 69 pages of document text analysed",
                ],
                totalItems: 42,
                findingCount: 42,
                gapCount: 11,
                expiresAt: new Date(
                  (report.submittedAt ?? Date.now()) + 72 * 60 * 60 * 1000,
                ).toISOString(),
                items: Array.from({ length: 10 }, (_, i) => ({
                  text: `Finding ${i + 1}: obligation statement excerpt from your document.`,
                  section: `Section ${i + 1}`,
                  classification:
                    i % 3 === 0 ? "notCovered" : i % 3 === 1 ? "partiallyCovered" : "covered",
                  coverageScore: Math.round((i % 3) * 35 + 10),
                  rationale:
                    "Sample rationale explaining the coverage assessment for this finding.",
                })),
              }
            : {
                title: "Your obligations preview is ready",
                summary:
                  "Preview: 10 obligation example(s). Full register: 239 obligations identified.",
                highlights: [
                  "239 obligations identified",
                  "1 document analysed",
                  "About 28 pages of document text analysed",
                ],
                totalItems: 239,
                findingCount: 239,
                expiresAt: new Date(
                  (report.submittedAt ?? Date.now()) + 72 * 60 * 60 * 1000,
                ).toISOString(),
                items: Array.from({ length: 10 }, (_, i) => ({
                  text: `Obligation ${i + 1}: regulatory requirement excerpt from your document.`,
                  section: `Section ${i + 1}`,
                })),
              }
          : undefined,
      });
    },

    async createCheckoutSession(
      req: CreateCheckoutSessionRequest,
    ): Promise<CreateCheckoutSessionResponse> {
      const report = getReport(req.reportId);
      if (report) upsert({ ...report, paymentState: "pending" });
      return delay({ checkoutUrl: req.successUrl });
    },

    async confirmStripeSession(
      req: ConfirmStripeSessionRequest,
    ): Promise<ConfirmStripeSessionResponse> {
      void req;
      const report = Object.values(readStore()).find((r) => r.paymentState === "pending");
      if (report) markReportPaidMock(report.reportId);
      return delay({
        reportId: report?.reportId ?? "unknown",
        paymentState: "paid" as PaymentState,
        status: "paid" as ReportStatus,
      });
    },
  };
}
